import { computed } from 'vue';
import { useStorage } from '@vueuse/core';
import CryptoJS from 'crypto-js';
import type { EKeyOfLocalStorage } from '@/types/localstroage.types';
import { VITE_SECRET_KEY as secretAccessKey } from '@/utils/runtimeConfig';

/**
 * Hook pour stocker des données dans le localStorage de manière chiffrée.
 * @param {EKeyOfLocalStorage} key - La clé sous laquelle les données sont stockées.
 * @param {any} initialValue - Valeur par défaut si aucune donnée n'est trouvée.
 * @returns {import('vue').ComputedRef<any>} Un ref réactif pour lire et écrire des données chiffrées.
 */
export function useEncryptedStorage(
  key: EKeyOfLocalStorage,
  initialValue: any,
) {
  // Utilisation de useStorage pour un accès réactif au localStorage
  const rawData = useStorage(key, null);

  // Ref calculé pour gérer le chiffrement et le déchiffrement
  const encryptedData = computed({
    get() {
      if (!rawData.value) return initialValue;
      try {
        // Déchiffrement avec CryptoJS
        const bytes = CryptoJS.AES.decrypt(rawData.value, secretAccessKey);
        return JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
      } catch {
        // Retourne la valeur initiale si déchiffrement impossible
        return initialValue;
      }
    },
    set(val) {
      // Chiffrement avec CryptoJS avant stockage
      rawData.value = CryptoJS.AES.encrypt(
        JSON.stringify(val),
        secretAccessKey,
      ).toString();
    },
  });

  return encryptedData;
}
